<template>
    <div id="quote-setup" :class="{'loading' : componentData.loading}">
        <div class="left">
            <div class="Heading">
                <Icon name="filter" />
                <div class="h3">{{ trans("Filters") }} </div>
            </div>
            <div class="element searchField">
                <input type="text" v-model="searchRef" placeholder="Search" />
            </div>
            <template v-for="refine in customComponentData.settings.refine_configuration">
                <div class="element" :class="{ [`${ucfirst(refine)}Field`]: true }">
                    <div ref="dropdown" class="dropdown">
                        <button class="btn dropdown-toggle" type="button" @click.dropdown="toggleDropdown(refine)">
                            <span class="title">{{ refine.title }}</span>
                        </button>
                        <div class="dropdown-menu" :class="{'open' : isDropdownOpen(refine)}">
                            <div :class="{ [`${ucfirst(refine)}_container`]: true }">
                                <div :class="{ [`${ucfirst(refine)}_element`]: true }" v-for="(option, key) in options(refine)">
                                    <input
                                        :type="refine.type"
                                        :class="refine.type"
                                        :name="field_name(refine)"
                                        :id="`${field_name(refine)}_${option}`"
                                        :value="key"
                                        :checked="checked(key,refine)"
                                    />
                                    <label :for="`${field_name(refine)}_${option}`" class="label" @click.prevent="handleChange($event, option, refine)">
                                        <span class="title">{{ option }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
        <div class="middle">
            <table width="100%">
                <thead>
                <tr>
                    <th class="td_code" align="left">{{ trans("Code") }}</th>
                    <th class="td_type" align="left">{{ trans("Type") }}</th>
                    <th class="td_name" align="left">{{ trans("Omschrijving") }}</th>
                    <th class="td_amount" align="left"></th>
                </tr>
                </thead>
                <tbody>
                <tr v-if="!componentData.loading && (componentData.items.length === 0) && (refine_filters.length > 0)">
                    <td colspan="4" align="center" class="td_name">
                        <span class="noproducts">{{ trans("Geen resultaten gevonden") }}</span>
                    </td>
                </tr>
                <tr v-else v-for="(item, index) in componentData.items" :key="index">
                    <td class="td_code"><span class="title">{{ getValue("code",item) }}</span></td>
                    <td class="td_type"><span class="title">{{ getValue("type",item) }}</span></td>
                    <td class="td_name">
                        <span class="title">{{ getValue("name",item) }}</span>
                        <template v-if="getValue('explanation',item)">
                            <TooltipElement :tooltip_information="getValue('explanation',item)" />
                        </template>
                    </td>
                    <td class="td_amount">
                        <div class="amount">
                        <input type="number" v-model.number="item.quantity">
                        <button class="button" type="button" @click="addToSelected(item)">
                            <Icon name="add"></Icon>
                        </button>
                        </div>
                    </td>
                </tr>
                </tbody>
            </table>
            <Pagination
                :id="settings.id"
                :pagination_key="PAGE_KEY"
                :pagination_total="settings.total"
                :pagination_limit="settings.limit"
                :pagination_current="settings.current"
                @updatePage="handlePagination"
            />
        </div>
        <div class="right">
            <div class="Heading">
                <div class="h3">{{ trans("Product list") }} </div>
            </div>
            <table width="100%" cellpadding="0" cellspacing="0" v-if="componentData.selectedItems.length > 0">
                <thead>
                <tr>
                    <th class="td_amount" align="left"></th>
                    <th class="td_code" align="left">{{ trans("Code") }}</th>
                    <th class="td_name" align="left">{{ trans("Omschrijving") }}</th>
                    <th class="td_icons" align="left"></th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in componentData.selectedItems">
                    <td class="td_amount"><span class="title">{{ item.quantity }}<small style="padding-left:1px;">x</small></span></td>
                    <td class="td_code"><span class="title">{{ item.code }}</span></td>
                    <td class="td_name"><span class="title">{{ item.description }}</span></td>
                    <td class="td_icons" align="right">
                        <button type="button" @click="removeFromSelected(item)">
                            <Icon name="delete"></Icon>
                        </button>
                    </td>
                </tr>
                </tbody>
            </table>
            <div v-else>
                <span class="noproducts">{{ trans("Nog geen items toegevoegd") }}</span>
            </div>
            <div class="element TextareaField">
                <label for="notes">
                    <span class="title" v-html='trans("Kun je het item niet vinden? <br>Plaats de productcode en het aantal hier.")' />
                </label>
                <textarea class="input textarea" id="notes" v-model="componentData.notes"></textarea>
            </div>
            <nav>
                <button type="button" class="more" @click.prevent="handleSubmit">
                    <span class="title">{{ trans(customComponentData.button_title ?? "Offerte aanvragen") }}</span>
                </button>
                <button type="button" class="more outline" @click.prevent="handleAsDraft" v-if="hasDraftButton">
                    <span class="title">{{ trans("Opslaan als concept") }}</span>
                </button>
            </nav>
        </div>
    </div>
</template>

<script>
import {defineComponent, reactive, ref, watch, onMounted, inject, computed} from "vue";

import trans from "helpers/_translation";
import Icon from "components/icon";
import Popup from "components/popup";
import { config, env, apiHostname } from "config";
import { getData, getDataAsync, getDataWithoutLimit } from "helpers/_api";
import Pagination from "js/components/pagination.vue";

import { addNotification } from "store/notifications";

import {hasQuery, getQuery, setQuery, getUrlWithQueryItemReplaced} from "helpers/_structure";

import TooltipElement from "formbuilder/elements/TooltipElement.vue";

export default defineComponent({
    name: "Quote Setup",
    props: {
        data: {
            type: Object,
            required: true
        },
        values: {
            type: Object,
            required: false
        }
    },
    components: {
        Icon,
        Popup,
        Pagination,
        TooltipElement
    },
    emits: ["handleCustomSubmit", "handleCustomDraft"],
    setup(props, { emit }) {

        // Define our parameters
        const componentData = reactive({
            items: [],
            totals: 0,
            loading: false,
            notes: "",
            selectedItems: [],
            refines: []
        });

        const filtersData = reactive({});

        const searchRef = ref("");
        const amountRef = ref();

        const PAGE_KEY = props.module_object === "Product" ? "page" : `page_${props.id}`;

        // Set settings and watch for props changes
        const settings = reactive({
            loading: true,
            current: hasQuery(PAGE_KEY) ? getQuery(PAGE_KEY) : 1,
            limit: 10,
            total: computed(() => componentData.totals),
            sortby: {
                seq: getQuery("seq") ?? null,
                order: getQuery("order") ?? null
            },
            popover: {
                open: false
            },
            template: {}
        });

        const handlePagination = (page, force = false) => {
            if (`${settings.current}` === `${page}` && !force) return;
            settings.current = page;
            //scrollToTop();
            fetchData();
        };

        // Fetch the data
        const appendUrlDefaults = (url) => {
            url = getUrlWithQueryItemReplaced("page", settings.current, url);
            ["seq", "order"].forEach(key => {
                if (!settings.sortby[key]) return;
                url = getUrlWithQueryItemReplaced(key, settings.sortby[key], url);
            });
            return url;
        };

        const customComponentData = computed(() =>  {
            return props.data;
        });


        const refine_filters = computed(() => {
            const result = [];


            if (searchRef.value) {
                // NOTE: ALWAYS ADD THIS FILTER AS FIRST FILTER, OPTIMIZES THE QUERY SPEED
                // NOTE: ALWAYS ADD THIS FILTER AS FIRST FILTER, OPTIMIZES THE QUERY SPEED
                // NOTE: ALWAYS ADD THIS FILTER AS FIRST FILTER, OPTIMIZES THE QUERY SPEED
                result.push([
                    "StudioIbizz\\Workflowproperty\\Filters\\WorkflowpropertySearch",
                    {
                        "column": ["artikel_code", "artikel_name", "artikel_type"],
                        "value": searchRef.value.split(" ").join(" AND ")
                    }
                ]);
            }

            // Loop through our refine
            Object.keys(filtersData).forEach(key => {
                const id = key.replace("field_", "");
                const property = optionData[`${id}`];
                if (!filtersData[key].length) return;
                result.push([
                    "StudioIbizz\\Workflowproperty\\Filters\\Workflowproperty",
                    {
                        "column": property.name,
                        "value": filtersData[key]
                    }
                ]);
            });

            return result;
        });
        const fetchData = () => {
            componentData.items = [];
            componentData.loading = true;


            const url = appendUrlDefaults(`${apiHostname}/api/v1.0/workflowcard?limit=${settings.limit}`);

            const extra_filters = customComponentData.value.settings.extra_filters;
            const selected_filters = refine_filters.value ?? [];
            getData(url, [
                ...selected_filters,
                [
                    "FieldFilter",
                    {
                        "column": "state",
                        "value": "Online"
                    }
                ],
                ...extra_filters
            ]).then(result => {
                componentData.items = result.data;
                componentData.totals = result._meta.total;
                setTimeout(() => {
                    componentData.loading = false;
                }, 250);
            });
        }

        const optionData = reactive({});
        const options = (refine) => {
            return computed(() => {
                if (!(`${refine.property_id}` in optionData)) {
                    optionData[`${refine.property_id}`] = [];
                    getDataAsync(`${apiHostname}/api/v1.0/workflowpropertyfield/${refine.property_id}`).then(result => {
                        optionData[`${refine.property_id}`] = result.data;
                    });
                }

                try {
                    const optionJson = optionData[`${refine.property_id}`].options_json;
                    if (!optionJson) {
                        return {};
                    }
                    const parsedOptionJson = JSON.parse(optionJson);
                    return parsedOptionJson;
                } catch (error) {
                    console.error("Failed to parse JSON:", error.message);
                }
                return {};
            }).value;
        };

        const getValue = (key = "name", item) => {
            const keyMap = {
                "name": 92,
                "code": 90,
                "type": 91,
                "explanation": 121
            };
            return computed(() => {
                if (key in keyMap) {
                    return item?.extra?.workflowproperties.find((property) => property.field.id == keyMap[key])?.value ?? "";
                }
                return "";
            }).value;
        };

        const getCode = (item) => {
            return computed(() => {
                if (item?.extra?.workflowproperties.find((property) => property.field.id == 90)) {
                    return item?.extra?.workflowproperties.find((property) => property.field.id == 90).value ?? "";
                } else{
                    return "";
                }
            }).value;
        };

        const getType = (item) => {
            return computed(() => {
                if (item?.extra?.workflowproperties.find((property) => property.field.id == 91)) {
                    return item?.extra?.workflowproperties.find((property) => property.field.id == 91).value ?? "";
                } else{
                    return "";
                }
            }).value;
        };

        const getName = (item) => {
            return computed(() => {
                if (item?.extra?.workflowproperties.find((property) => property.field.id == 92)) {
                    return item?.extra?.workflowproperties.find((property) => property.field.id == 92).value ?? "";
                } else{
                    return "";
                }
            }).value;
        };

        const getNote = (item) => {
            return computed(() => {
                if (item?.extra?.workflowproperties.find((property) => property.field.id == 121)) {
                    return item?.extra?.workflowproperties.find((property) => property.field.id == 121).value ?? "";
                } else{
                    return "";
                }
            }).value;
        };

        const addToSelected = (item) => {
            const quantityToAdd = item.quantity || 1; // Default to 1 if no value is entered

            // Check if the item is already in selectedItems
            const existingItem = componentData.selectedItems.find(selectedItem => {
                return selectedItem.code === getValue("code",item);
            });

            if (existingItem) {
                existingItem.quantity += quantityToAdd; // Increase the amount by the entered quantity
            } else {
                componentData.selectedItems.push({
                    quantity: quantityToAdd,
                    code: getValue("code",item),
                    description: getValue("name",item)
                });
                item.quantity = "";
            }
        };

        const removeFromSelected = (item) => {
            componentData.selectedItems = componentData.selectedItems.filter((selectedItem) => selectedItem != item);
        };

        onMounted(() => {
            let timeoutFunction = null;
            watch(
                () => searchRef.value,
                (newValue, oldValue) => {
                    timeoutFunction && clearTimeout(timeoutFunction);
                    timeoutFunction = setTimeout(fetchData, newValue !== oldValue ? 1000 : 0);
                },
                {
                    immediate: true,
                    deep: true
                }
            );
        });

        const handleSubmit = (e) => { 
            if (!componentData.selectedItems.length && !componentData.notes) {
                addNotification(trans("Selecteer alsjeblieft een %s van de lijst of vul het opmerkingen veld in", trans("item")));
                return;
            }

            const json = {
                items: componentData.selectedItems,
                notes: componentData.notes
            };

            emit("handleCustomSubmit", JSON.stringify(json));
        };
        const handleAsDraft = (e) => {
            const json = {
                items: componentData.selectedItems,
                notes: componentData.notes
            };
            emit("handleCustomDraft", JSON.stringify(json));
        };

        const dropdownData = reactive({});

        const isDropdownOpen  = (refine) => {
            return computed(() => {
                // Set open if it is suppose to be or when first item
                if (refine.open) {
                    dropdownData[`${refine.property_id}`] = true;
                }
                return dropdownData[`${refine.property_id}`] ?? false;
            }).value;
        };
        const toggleDropdown = (refine) => {
            dropdownData[`${refine.property_id}`] = !isDropdownOpen(refine);
        };

        const predefined_values = computed(() => {
            return [];
        });

        // computed to define if it should be checked or not
        const checked = (key,refine) => {
            return computed(() => {
                const name = field_name(refine);
                return filtersData[name]?.includes(`${key}`);
            }).value;
        }
        const field_name = (refine) => {
            return computed(() => {
                return `field_${refine.property_id}`;
            }).value;
        }
        const ucfirst = (refine) => {
            return computed(() => {
                return refine.type.charAt(0).toUpperCase() + refine.type.slice(1);
            }).value;
        }

        const handleChange = (e, key, refine) => {
            const name = field_name(refine);

            if (!filtersData[name]) {
                filtersData[name] = [];
            }
            if (checked(key,refine)) {
                filtersData[name] = filtersData[name].filter(value => `${value}` !== `${key}`);
            } else {
                if (props.type === "radio") {
                    filtersData[name] = [`${key}`];
                } else {
                    filtersData[name].push(`${key}`);
                }
            }
        };

        watch(
            () => filtersData,
            () => {
                fetchData();
            },
            {
                deep: true
            }
        );

        const hasDraftButton = computed(() => {
            return !!customComponentData.value?.draft_property_id;
        });

        let init = false;
        watch(
            () => props.values,
            () => {
                if (!customComponentData.value?.settings?.filled_by_property_name) return;
                let targetValue = Object.values(props.values.value).find(a => a.field.name === customComponentData.value.settings.filled_by_property_name);
                console.log("targetValue",targetValue);
                if (targetValue && targetValue.value?.items) {
                    componentData.selectedItems = targetValue.value.items;
                    if (!init) {
                        componentData.notes = targetValue.value.notes;
                        init = true;
                    }
                }
            },
            {
                deep: true,
                immediate: true
            }
        );

        return {
            trans,

            componentData,

            settings,
            handlePagination,

            addToSelected,
            removeFromSelected,

            searchRef,
            amountRef,

            getValue,

            handleSubmit,
            handleAsDraft,

            options,
            customComponentData,

            isDropdownOpen,
            toggleDropdown,
            handleChange,

            field_name,
            ucfirst,
            checked,

            hasDraftButton,

            refine_filters
        };
    },
});
</script>